import { answerQuestions } from "@/hooks/useRequest/useSurveyRequest";
import { AnswerQuestionModel, SurveyQuestionUISchema } from "@/interfaces/surveyTypes";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";
import { useTranslation } from "next-i18next";
import React, { useEffect, useState } from "react";
import SurveyTitle from "../SurveyTitleV2";
import SurveyFooter from "../SurveyFooter";
import SelectOptions, { Option } from "@/components/SelectOptions";
import ContentContainer from "@/containers/common/ContentContainer";

interface SexProps {
	answerFieldToValueMap: Map<string, string>;
	questionFieldToIdMap: Map<string, string>;
	questionFieldToUiSchemaMap: Map<string, string>;
	orcGender?: number;
	onNextClick: () => void;
}

function Sex(props: SexProps) {
	const { t } = useTranslation("survey");
	const [loading, setLoading] = useState(false);
	const [gender, setGender] = useState<string>();
	const [genderOptions, setGenderOptions] = useState<Option[]>([]);

	useEffect(() => {
		const genderValue = props.answerFieldToValueMap.get("gender");
		if (!genderValue) return;
		setGender(genderValue);
	}, [props.answerFieldToValueMap]);

	useEffect(() => {
		try {
			const uiSchema: SurveyQuestionUISchema = JSON.parse(props.questionFieldToUiSchemaMap.get("gender") || "");
			const options = uiSchema.options.map((item) => {
				return {
					label: t(item.i18nKey),
					value: item.value,
				};
			});

			setGenderOptions(options);
		} catch (error) {}
	}, [props.questionFieldToUiSchemaMap, t]);

	const handleConfirm = async (_gender: string) => {
		if (!_gender) return;
		try {
			const genderQuestionId = props.questionFieldToIdMap.get("gender");
			if (!genderQuestionId) {
				throw Error("missing gender questionId");
			}

			const payload: AnswerQuestionModel[] = [
				{
					answer: _gender,
					questionId: genderQuestionId,
				},
			];

			setLoading(true);
			await answerQuestions({ payload });
			props.onNextClick();
		} catch (error) {
			console.log(error);
		} finally {
			setLoading(false);
			log2sensors(SensorsEventIds.AO_GENDER_NEXTPAGE);
		}
	};

	useEffect(() => {
		log2sensors(SensorsEventIds.AO_GENDER_VIEW);
	}, []);

	return (
		<ContentContainer
			animation
			header={
				<div className="px-4.5">
					<SurveyTitle
						title={t("what-is-your-gender")}
						subtitle={t("your-gender-should-match-the-ID-proof-you-upload")}
					/>
				</div>
			}
			footer={
				<div className="px-4.5">
					<SurveyFooter
						nextBtn={false}
						onPrivacyClick={() => log2sensors(SensorsEventIds.AO_GENDER_PRAVICY)}
						onSecureClick={() => log2sensors(SensorsEventIds.AO_GENDER_SECURITY)}
					/>
				</div>
			}
		>
			<div className="h-12" />
			<SelectOptions
				value={gender}
				options={genderOptions}
				loading={loading}
				onChange={(val) => {
					setGender(String(val));
					switch (val) {
						case "1":
							log2sensors(SensorsEventIds.AO_GENDER_MALE);
							break;
						case "2":
							log2sensors(SensorsEventIds.AO_GENDER_FEMALE);
							break;
						case "9":
							log2sensors(SensorsEventIds.AO_GENDER_OTHER);
							break;
					}
					handleConfirm(String(val));
				}}
			/>
		</ContentContainer>
	);
}

export default Sex;
